/**
 * GeneralDataTab — extracted from CustomerForm.js for readability.
 * Receives everything via a single `ctx` bag so the parent keeps
 * full ownership of state management.
 */
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertCircle, ArrowLeft, Edit, Loader2 } from 'lucide-react';

const GeneralDataTab = ({ ctx }) => {
  const {
    formData,
    handleInputChange,
    handleAccountGroupChange,
    validateCustomerIdRealTime,
    customerIdError,
    numberRangeInfo,
    loadingNumberRange,
    accountGroups,
    countries,
    languages,
    mode,
    readOnly,
    customerId,
    loading,
    setActiveTab,
    navigate,
  } = ctx;

  return (
    <>
                <div className="space-y-6">
                    {/* Account Group Selection */}
                    <Card className="bg-amber-50 border-amber-200">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-600" />
                          Account Group Selection
                        </CardTitle>
                        <CardDescription>
                          Select Account Group first - this determines the number range and field layout
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Account Group *</Label>
                            <Select
                              value={formData.account_group_id}
                              onValueChange={handleAccountGroupChange}
                              disabled={readOnly}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select Account Group" />
                              </SelectTrigger>
                              <SelectContent>
                                {accountGroups.map((ag) => (
                                  <SelectItem key={ag.account_group_id} value={ag.account_group_id}>
                                    {ag.account_group_id} - {ag.description}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {mode === 'create' && (
                            <>
                              {loadingNumberRange ? (
                                <div className="flex items-center justify-center">
                                  <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
                                </div>
                              ) : numberRangeInfo ? (
                                <>
                                  {numberRangeInfo.external ? (
                                    <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
                                      <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                                          <span className="text-amber-600 font-bold">!</span>
                                        </div>
                                        <div className="flex-1">
                                          <h4 className="text-sm font-semibold text-amber-900 mb-1">
                                            External Number Range - Manual Entry Required
                                          </h4>
                                          <p className="text-xs text-amber-800 mb-3">
                                            Number Range: <strong>{numberRangeInfo.number_range_id}</strong> ({numberRangeInfo.description || 'External Range'})
                                          </p>
                                          <div>
                                            <Label className="text-amber-900">Customer ID *</Label>
                                            <Input
                                              value={formData.customer_id}
                                              onChange={(e) => handleInputChange('customer_id', e.target.value.toUpperCase())}
                                              onBlur={(e) => {
                                                if (e.target.value) {
                                                  validateCustomerIdRealTime(e.target.value);
                                                }
                                              }}
                                              placeholder={
                                                numberRangeInfo.prefix 
                                                  ? `${numberRangeInfo.prefix}XXXXXXXX (e.g., ${numberRangeInfo.prefix}12345678)`
                                                  : "Enter Customer ID"
                                              }
                                              maxLength={20}
                                              required
                                              className={`mt-1 border-amber-300 focus:border-amber-500 focus:ring-amber-500 ${customerIdError ? 'border-red-500' : ''}`}
                                            />
                                            {customerIdError ? (
                                              <p className="mt-2 text-xs text-red-600 font-medium flex items-center gap-1">
                                                <AlertCircle className="w-3 h-3" />
                                                {customerIdError}
                                              </p>
                                            ) : (
                                              <div className="mt-2 text-xs text-amber-700">
                                                {numberRangeInfo.prefix ? (
                                                  <>
                                                    <div className="font-semibold mb-1">📋 Required Format:</div>
                                                    <div className="bg-amber-100 border border-amber-300 rounded px-2 py-1 font-mono">
                                                      <span className="text-amber-900 font-bold">{numberRangeInfo.prefix}</span>
                                                      <span className="text-gray-600"> (prefix) + </span>
                                                      <span className="text-amber-900 font-bold">########</span>
                                                      <span className="text-gray-600"> (8 digits)</span>
                                                    </div>
                                                    <div className="mt-1">
                                                      ✓ Total: {numberRangeInfo.prefix.length + 8} characters | Example: <strong>{numberRangeInfo.prefix}12345678</strong>
                                                    </div>
                                                  </>
                                                ) : (
                                                  <div>✓ Enter any unique Customer ID</div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div>
                                      <Label>
                                        Customer ID (Auto-Generated)
                                      </Label>
                                      <Input
                                        value={formData.customer_id}
                                        placeholder='Will be auto-generated'
                                        maxLength={20}
                                        disabled={true}
                                        className="mt-1 bg-gray-50"
                                      />
                                      <p className="text-xs text-gray-500 mt-1">
                                        Internal Numbering: Next = {String(parseInt(numberRangeInfo.current_number) + 1).padStart(10, '0')}
                                      </p>
                                    </div>
                                  )}
                                </>
                              ) : null}
                            </>
                          )}

                          {mode === 'view' && (
                            <div>
                              <Label>Customer ID</Label>
                              <Input
                                value={formData.customer_id}
                                disabled={true}
                                className="mt-1 bg-gray-50 font-medium"
                              />
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Customer Identification */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                        Customer Identification
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Title *</Label>
                          <Select
                            value={formData.title}
                            onValueChange={(value) => handleInputChange('title', value)}
                            disabled={readOnly}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Company">Company</SelectItem>
                              <SelectItem value="Mr.">Mr.</SelectItem>
                              <SelectItem value="Ms.">Ms.</SelectItem>
                              <SelectItem value="Mrs.">Mrs.</SelectItem>
                              <SelectItem value="Dr.">Dr.</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Customer Name *</Label>
                          <Input
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Legal Entity Name"
                            maxLength={100}
                            className="mt-1"
                            disabled={readOnly}
                          />
                        </div>

                        <div>
                          <Label>Search Term</Label>
                          <Input
                            value={formData.search_term}
                            onChange={(e) => handleInputChange('search_term', e.target.value.toUpperCase())}
                            placeholder="e.g., ACME, XYZ"
                            maxLength={20}
                            className="mt-1"
                            disabled={readOnly}
                          />
                          <p className="text-xs text-gray-500 mt-1">Short code for quick searching</p>
                        </div>
                      </div>
                    </div>

                    {/* Address Information */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                        Address Information
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <Label>Street / House Number *</Label>
                          <Input
                            value={formData.street_house_no}
                            onChange={(e) => handleInputChange('street_house_no', e.target.value)}
                            placeholder="123 Main Street"
                            maxLength={100}
                            className="mt-1"
                            disabled={readOnly}
                          />
                        </div>

                        <div>
                          <Label>Postal Code *</Label>
                          <Input
                            value={formData.postal_code}
                            onChange={(e) => handleInputChange('postal_code', e.target.value)}
                            placeholder="12345"
                            maxLength={10}
                            className="mt-1"
                            disabled={readOnly}
                          />
                        </div>

                        <div>
                          <Label>City *</Label>
                          <Input
                            value={formData.city}
                            onChange={(e) => handleInputChange('city', e.target.value)}
                            placeholder="New York"
                            maxLength={50}
                            className="mt-1"
                            disabled={readOnly}
                          />
                        </div>

                        <div>
                          <Label>Country *</Label>
                          <Select
                            value={formData.country}
                            onValueChange={(value) => handleInputChange('country', value)}
                            disabled={readOnly}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Country" />
                            </SelectTrigger>
                            <SelectContent>
                              {countries.map((country) => (
                                <SelectItem key={country.country_code} value={country.country_code}>
                                  {country.country_code} - {country.country_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Region / State *</Label>
                          <Input
                            value={formData.region}
                            onChange={(e) => handleInputChange('region', e.target.value.toUpperCase())}
                            placeholder="CA, NY, TX"
                            maxLength={3}
                            className="mt-1"
                            disabled={readOnly}
                          />
                          <p className="text-xs text-gray-500 mt-1">State/Province code</p>
                        </div>
                      </div>
                    </div>

                    {/* Communication */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                        Communication
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Language *</Label>
                          <Select
                            value={formData.language}
                            onValueChange={(value) => handleInputChange('language', value)}
                            disabled={readOnly}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {languages.map((lang) => (
                                <SelectItem key={lang.language_code} value={lang.language_code}>
                                  {lang.language_code} - {lang.language_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Mobile Number</Label>
                          <Input
                            value={formData.mobile_number}
                            onChange={(e) => handleInputChange('mobile_number', e.target.value)}
                            placeholder="+1 234 567 8900"
                            maxLength={20}
                            className="mt-1"
                            disabled={readOnly}
                          />
                        </div>

                        <div className="col-span-2">
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="contact@company.com"
                            maxLength={100}
                            className="mt-1"
                            disabled={readOnly}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between pt-4 border-t">
                      {mode === 'create' && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate('/customer-master')}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={loading || !formData.account_group_id}
                            className="bg-orange-600 hover:bg-orange-700"
                          >
                            {loading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Creating...
                              </>
                            ) : (
                              'Create Customer'
                            )}
                          </Button>
                        </>
                      )}

                      {mode === 'view' && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate('/view-customers')}
                          >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to List
                          </Button>
                          <Button
                            type="button"
                            onClick={() => navigate(`/customer-edit/${customerId}`)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Customer
                          </Button>
                        </>
                      )}

                      {mode === 'edit' && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate(`/customer-view/${customerId}`)}
                          >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={loading || !formData.account_group_id}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {loading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Updating...
                              </>
                            ) : (
                              'Update Customer'
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
    </>
  );
};

export default GeneralDataTab;
